import React, { useState, useEffect } from 'react';
import { DownloadIcon, LoadingSpinner } from './icons';
import { downscaleImage } from '../utils/watermarkUtils';

// data URLをBlobに変換する関数
const dataURLToBlob = (dataURL: string): Blob => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};


// ギャラリーと同じシンプルなダウンロード関数（改善版）
const downloadImage = (imageUrl: string, filename: string) => {
  return new Promise<void>((resolve, reject) => {
    try {
      // まず直接リンクで試行
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = filename;
      link.style.display = 'none';
      
      // より確実なダウンロードのため、イベントリスナーを追加
      link.addEventListener('click', () => {
        console.log('✅ Direct download link clicked');
      });
      
      document.body.appendChild(link);
      
      // 少し遅延してからクリック（ブラウザの準備を待つ）
      setTimeout(() => {
        link.click();
        console.log('✅ Direct download initiated');
        
        // クリーンアップ
        setTimeout(() => {
          document.body.removeChild(link);
          resolve();
        }, 100);
      }, 50);
    } catch (error) {
      reject(error);
    }
  });
};

// fetch APIを使用したより確実なダウンロード関数
const downloadImageWithFetch = async (imageUrl: string, filename: string) => {
  try {
    console.log('🌐 Fetching image for download:', imageUrl.substring(0, 100) + '...');
    
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const blob = await response.blob();
    console.log('📦 Image blob created:', blob.size, 'bytes, type:', blob.type);
    
    // JPEG形式に変換する必要がある場合
    let finalBlob = blob;
    if (filename.endsWith('.jpg') || filename.endsWith('.jpeg')) {
      // PNG形式のblobをJPEG形式に変換
      const img = new Image();
      const imgUrl = URL.createObjectURL(blob);
      
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas context not available'));
            return;
          }
          
          ctx.drawImage(img, 0, 0);
          
          canvas.toBlob((convertedBlob) => {
            if (convertedBlob) {
              finalBlob = convertedBlob;
              URL.revokeObjectURL(imgUrl);
              resolve();
            } else {
              reject(new Error('Failed to convert to JPEG'));
            }
          }, 'image/jpeg', 0.95);
        };
        
        img.onerror = () => {
          URL.revokeObjectURL(imgUrl);
          reject(new Error('Failed to load image'));
        };
        
        img.src = imgUrl;
      });
    }
    
    const url = URL.createObjectURL(finalBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    
    setTimeout(() => {
      link.click();
      console.log('✅ Fetch-based download initiated');
      
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    }, 50);
    
  } catch (error) {
    console.error('❌ Fetch-based download failed:', error);
    throw error;
  }
};

// 画像をJPEG形式に変換してダウンロードする関数（CORS対応）
const downloadAsJPEG = (imageUrl: string, filename: string) => {
  return new Promise<void>((resolve, reject) => {
    const img = new Image();
    
    // CORSエラーを避けるため、crossOriginを設定しない
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }
        
        ctx.drawImage(img, 0, 0);
        
        // JPEG形式でエクスポート（品質95%）
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';
            
            // より確実なダウンロードのため、イベントリスナーを追加
            link.addEventListener('click', () => {
              console.log('✅ Download link clicked');
            });
            
            document.body.appendChild(link);
            
            // 少し遅延してからクリック（ブラウザの準備を待つ）
            setTimeout(() => {
              link.click();
              console.log('✅ Download initiated');
              
              // クリーンアップ
              setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                resolve();
              }, 100);
            }, 50);
          } else {
            reject(new Error('Failed to create blob'));
          }
        }, 'image/jpeg', 0.95);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    img.src = imageUrl;
  });
};

interface ImageViewerProps {
  originalImage: string;
  generatedImage: string | null;
  isLoading: boolean;
  isPaidUser?: boolean;
  currentPrompt?: string;
  userId?: string;
}

const ImageCard: React.FC<{
  label: string;
  imageUrl: string;
  children?: React.ReactNode;
}> = ({ label, imageUrl, children }) => (
  <div className="relative w-full aspect-square bg-white rounded-lg overflow-hidden border border-slate-200 flex flex-col shadow-sm">
    <div className="absolute top-2 left-2 bg-slate-900/60 text-white text-xs font-semibold px-2 py-1 rounded-full">
      {label}
    </div>
    <img src={imageUrl} alt={label} className="w-full h-full object-cover" />
    {children}
  </div>
);

export const ImageViewer: React.FC<ImageViewerProps> = ({ originalImage, generatedImage, isLoading, isPaidUser = false, currentPrompt = '', userId }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('');

  // 画像生成中の進行状況を更新
  useEffect(() => {
    if (!isLoading) {
      setGenerationProgress(0);
      setGenerationStep('');
      return;
    }

    // ローディング開始時にリセット
    setGenerationProgress(0);
    setGenerationStep('画像を分析中...');

    const steps = [
      { progress: 20, text: '画像を分析中...' },
      { progress: 40, text: 'AIが生成中...' },
      { progress: 60, text: '画像を処理中...' },
      { progress: 80, text: '最終調整中...' },
      { progress: 95, text: '仕上げ中...' }
    ];

    let currentStepIndex = 0;
    const interval = setInterval(() => {
      if (currentStepIndex < steps.length) {
        setGenerationProgress(steps[currentStepIndex].progress);
        setGenerationStep(steps[currentStepIndex].text);
        currentStepIndex++;
      } else {
        // 最後のステップで95%に留める
        if (generationProgress < 95) {
          setGenerationProgress(95);
        }
      }
    }, 4000); // 4秒ごとに更新

    return () => clearInterval(interval);
  }, [isLoading]);

  // 表示用画像は常に元の画像を使用
  const displayImage = isLoading ? null : generatedImage;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 w-full">
      <ImageCard label="元画像" imageUrl={originalImage} />
      
      <div className="relative w-full aspect-square bg-white rounded-lg overflow-hidden border border-slate-200 flex flex-col items-center justify-center shadow-sm">
        <div className="absolute top-2 left-2 bg-slate-900/60 text-white text-xs font-semibold px-2 py-1 rounded-full z-10">
          生成画像
        </div>

        {isLoading && (
          <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center text-center p-4 z-20 backdrop-blur-sm">
            <LoadingSpinner className="w-10 h-10 mb-4 text-blue-600" />
            
            {/* 進行状況表示 */}
            <div className="mb-4 w-full max-w-md">
              <p className="font-semibold text-base sm:text-lg text-slate-900 mb-2">{generationStep || '宣材写真を生成しています...'}</p>
              <div className="w-full bg-slate-200 rounded-full h-2 mb-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${generationProgress}%` }}
                ></div>
              </div>
              <p className="text-slate-500 text-sm">{generationProgress}%</p>
            </div>
            
            {/* プロンプト表示エリア */}
            {currentPrompt && (
              <div className="mt-4 bg-white rounded-lg p-3 border max-w-md w-full">
                <h4 className="text-xs font-medium text-slate-700 mb-2">送信中のプロンプト:</h4>
                <div className="bg-slate-50 rounded p-2 max-h-20 overflow-y-auto">
                  <p className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed">
                    {currentPrompt}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {displayImage ? (
          <>
            {/* iOS/Androidともネイティブの長押し→画像に保存を使うため、自作ハンドラは付けない */}
            <img 
              src={displayImage} 
              alt="Generated" 
              className="w-full h-full object-cover"
            />
            
            {/* 画像右下のダウンロードボタン（ギャラリー風） */}
            <div className="absolute bottom-3 right-3 z-10">
              <button
                onClick={async () => {
                  if (isDownloading) return;
                  
                  setIsDownloading(true);
                  try {
                    let imageToDownload = displayImage;
                    const filename = 'ai-headshot.jpg';
                    
                    if (!isPaidUser && displayImage) {
                      try {
                        imageToDownload = await downscaleImage(displayImage, 0.5);
                      } catch (downscaleError) {
                        console.error('❌ ダウンロード用リサイズエラー:', downscaleError);
                        imageToDownload = displayImage;
                      }
                    }
                    
                    try {
                      await downloadAsJPEG(imageToDownload, filename);
                    } catch (error) {
                      try {
                        await downloadImageWithFetch(imageToDownload, filename);
                      } catch (fetchError) {
                        await downloadImage(imageToDownload, filename);
                      }
                    }
                  } catch (error) {
                    console.error('❌ Download failed:', error);
                    try {
                      await downloadImage(displayImage, 'ai-headshot.jpg');
                    } catch (fallbackError) {
                      console.error('❌ Fallback download also failed:', fallbackError);
                      alert('ダウンロードに失敗しました。ブラウザの設定を確認してください。');
                    }
                  } finally {
                    setIsDownloading(false);
                  }
                }}
                className={`flex items-center justify-center w-10 h-10 rounded-full shadow-lg transition-all backdrop-blur-sm ${
                  isDownloading 
                    ? 'bg-blue-100 text-blue-600 cursor-not-allowed' 
                    : 'bg-white/90 hover:bg-white text-slate-700 hover:text-blue-600 hover:shadow-xl hover:scale-110'
                }`}
                title={isDownloading ? "ダウンロード中..." : "画像をダウンロード"}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <LoadingSpinner className="w-4 h-4" />
                ) : (
                  <DownloadIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </>
        ) : (
          !isLoading && (
            <div className="text-center text-slate-500">
              <p>生成された宣材写真がここに表示されます。</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};